/**
 * The contact page's binding of the shared detail-page surface.
 *
 * Everything structural — the shell, the drag, the rail, the persistence —
 * lives in `shared/surface`. This file is the whole of what contacts add:
 * their config (`./layout`) and one typed hook over the generic one.
 *
 * It binds the SELECTION-FREE hook: the contact page has no detail pane, so it
 * neither names a selection kind nor publishes a `select` that would open a
 * pane it never declared. It DOES have a reader, so the reader third of the
 * API is typed — see `ContactSurfaceContext`.
 */

import {
  useSurfaceWithoutSelection,
  UseDetailSurfaceOptions,
  UseSurfaceWithoutSelectionResult,
} from '../../shared/surface/useDetailSurface';
import { CONTACT_SURFACE, CONTACT_TABS, ContactPanelId, ContactTab } from './layout';
import { ContactReaderContent } from './ContactSurfaceContext';

export { CONTACT_SURFACE, CONTACT_TABS };
export type { ContactPanelId, ContactTab };

export type ContactWorkspace = UseSurfaceWithoutSelectionResult<
  ContactPanelId,
  ContactReaderContent,
  ContactTab
>;

/**
 * The contact page's layout, reader and tab state.
 *
 * Takes the contact's id: the surface is entity-scoped, so each contact
 * remembers its own arrangement — see the note at the top of `./layout`.
 */
export const useContactWorkspace = (
  options: UseDetailSurfaceOptions<ContactTab> = {}
): ContactWorkspace =>
  useSurfaceWithoutSelection<ContactPanelId, ContactReaderContent, ContactTab>(
    CONTACT_SURFACE,
    options
  );
